import { useState, useEffect } from 'react';

// Estilos personalizados para react-select (tema oscuro)
export const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.7)',
        borderColor: state.isFocused ? 'rgba(140, 220, 255, 0.5)' : 'rgba(140, 220, 255, 0.15)',
        borderRadius: '8px',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(140, 220, 255, 0.3)' : 'none',
        '&:hover': {
            borderColor: 'rgba(140, 220, 255, 0.3)'
        }
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.98)',
        border: '1px solid rgba(140, 220, 255, 0.2)',
        borderRadius: '8px',
        zIndex: 100
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
        '::-webkit-scrollbar': {
            width: '6px'
        },
        '::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb': {
            background: 'rgba(140, 220, 255, 0.3)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(140, 220, 255, 0.5)'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'rgba(140, 220, 255, 0.2)'
            : state.isFocused
                ? 'rgba(140, 220, 255, 0.1)'
                : 'transparent',
        color: state.isSelected ? '#8cdcff' : '#e0e6ed',
        cursor: 'pointer',
        fontSize: '13px',
        padding: '8px 12px',
        '&:active': {
            backgroundColor: 'rgba(140, 220, 255, 0.2)'
        }
    }),
    singleValue: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    input: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    placeholder: (base) => ({
        ...base,
        color: 'rgba(224, 230, 237, 0.5)',
        fontSize: '14px'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: 'rgba(140, 220, 255, 0.5)',
        padding: '6px',
        '&:hover': {
            color: 'rgba(140, 220, 255, 0.8)'
        }
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: 'rgba(140, 220, 255, 0.15)',
        borderRadius: '4px',
        border: '1px solid rgba(140, 220, 255, 0.25)'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '13px',
        padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: 'rgba(224, 230, 237, 0.5)',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(248, 113, 113, 0.3)',
            color: '#f87171'
        }
    }),
    groupHeading: (base) => ({
        ...base,
        color: '#8cdcff',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '6px 12px'
    }),
    clearIndicator: (base) => ({
        ...base,
        color: 'rgba(140, 220, 255, 0.5)',
        padding: '6px',
        '&:hover': {
            color: 'rgba(248, 113, 113, 0.8)'
        }
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999
    })
};

// Componente para input editable con guardado en blur
export const EditableInput = ({ value, onSave, type = 'text', placeholder = '', className = 'input', ...props }) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    const handleChange = (e) => {
        setLocalValue(e.target.value);
        setIsDirty(true);
    };

    const handleBlur = () => {
        if (isDirty && localValue !== value) {
            onSave(type === 'number' ? parseFloat(localValue) || 0 : localValue);
            setIsDirty(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    return (
        <input
            className={className}
            type={type}
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            {...props}
        />
    );
};

// Componente para inputs secretos (no muestra el valor actual)
export const SecretInput = ({ hasValue, onSave, onClear, placeholder = '' }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newValue, setNewValue] = useState('');

    const handleSave = () => {
        if (newValue.trim()) {
            onSave(newValue.trim());
            setNewValue('');
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setNewValue('');
        setIsEditing(false);
    };

    const handleClear = () => {
        if (window.confirm('¿Eliminar la URL configurada?')) {
            onClear();
        }
    };

    if (isEditing) {
        return (
            <div className="secret-input-edit">
                <input
                    className="input"
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder={placeholder}
                    autoFocus
                />
                <div className="secret-input-actions">
                    <button type="button" className="btn neon small" onClick={handleSave}>
                        Guardar
                    </button>
                    <button type="button" className="btn ghost small" onClick={handleCancel}>
                        Cancelar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="secret-input-display">
            {hasValue ? (
                <>
                    <span className="secret-value">••••••••••••••••••••••••</span>
                    <div className="secret-input-actions">
                        <button type="button" className="btn ghost small" onClick={() => setIsEditing(true)}>
                            Cambiar
                        </button>
                        <button type="button" className="btn ghost small danger" onClick={handleClear}>
                            Eliminar
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <span className="secret-empty">Sin configurar</span>
                    <button type="button" className="btn neon small" onClick={() => setIsEditing(true)}>
                        Configurar
                    </button>
                </>
            )}
        </div>
    );
};
